<script lang="ts">
  import { onMount } from "svelte";
  import DealEditSheet from "../components/DealEditSheet.svelte";
  import CallsBlock from "../components/CallsBlock.svelte";
  import ContactSheet from "../components/ContactSheet.svelte";
  import NoteSheet from "../components/NoteSheet.svelte";
  import StageSheet from "../components/StageSheet.svelte";
  import TaskCreateSheet, { type TeamMember } from "../components/TaskCreateSheet.svelte";
  import { api, type DealCard, type DealPatch, type DealsMeta, type OpenTask } from "../lib/api";
  import { haptic } from "../lib/tg";

  let {
    leadAmoId,
    role = "unknown",
    onback,
    onhome,
  }: {
    leadAmoId: number;
    role?: "director" | "manager" | "installer" | "integration" | "unknown";
    onback?: () => void;
    onhome?: () => void;
  } = $props();

  let deal = $state<DealCard | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let pinging = $state<number | null>(null);
  let dealPinging = $state(false);
  let toast = $state<string | null>(null);
  let taskSheetOpen = $state(false);
  let team = $state<TeamMember[]>([]);
  let meta = $state<DealsMeta | null>(null);
  let stageSheetOpen = $state(false);
  let editSheetOpen = $state(false);
  let noteSheetOpen = $state(false);
  let contactSheetOpen = $state(false);
  let editingContact = $state<{ amo_id: number; name: string; phone: string | null } | null>(null);
  let moving = $state(false);

  // Optimistically-added tasks waiting to appear in server response.
  // Webhook → DB sync can take 5–30s — keep them visible meanwhile.
  let pendingTasks = $state<OpenTask[]>([]);

  function showToast(msg: string, ms = 3000) {
    toast = msg;
    setTimeout(() => { if (toast === msg) toast = null; }, ms);
  }

  async function pingTask(t: { amo_id: number; db_id?: number; responsible_name: string | null; text: string }) {
    // «Пнуть» = amoCRM-side poke: note в lead + срочная задача +1ч + (опц.) SMS.
    // Раньше делали TG push в бот менеджера через /api/tasks/{id}/ping — менеджеры
    // в боте не сидят, поэтому push никогда не доходил. Новый poke работает через amoCRM,
    // которая сама шлёт менеджеру email/push на новую ноту и задачу.
    if (!t.db_id) {
      showToast("Старый формат задачи — открой через /просрочки");
      return;
    }
    const tg = (window as any).Telegram?.WebApp;
    const confirmText = `Пнуть ${t.responsible_name ?? "менеджера"} по задаче?\n\n«${t.text.slice(0, 80)}»\n\nВ amoCRM появится заметка и срочная задача. Если не выполнит в течение 2 часов — автонапоминания каждые 2 часа (до 3 раз). После 3-х — скажу вам.`;
    let ok = false;
    if (tg?.showConfirm) {
      ok = await new Promise<boolean>((resolve) => tg.showConfirm(confirmText, (v: boolean) => resolve(!!v)));
    } else {
      ok = confirm(confirmText);
    }
    if (!ok) return;
    haptic("medium");
    pinging = t.amo_id;
    try {
      const r = await api.pokeTaskOwner(t.db_id);
      const parts: string[] = [];
      if (r.note_added) parts.push("заметка");
      if (r.urgent_task_amo_id) parts.push("срочная задача");
      if (r.sms && r.sms.ok) parts.push("SMS");
      const msg = `✓ Пнул ${r.manager_name}: ${parts.join(" + ") || "—"}`;
      if (tg?.showAlert) tg.showAlert(msg);
      else showToast(msg);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ошибка";
      if (tg?.showAlert) tg.showAlert(`Не удалось пнуть: ${msg}`);
      else showToast(msg);
    } finally {
      pinging = null;
    }
  }

  async function pingDeal() {
    if (!deal) return;
    const tg = (window as any).Telegram?.WebApp;
    const confirmText = `Пнуть ${deal.responsible_name ?? "менеджера"} по сделке «${deal.name.slice(0, 60)}»?\n\nВ amoCRM появится заметка и срочная задача. Если не отреагирует в течение 2 часов — автонапоминания каждые 2 часа (до 3 раз).`;
    let ok = false;
    if (tg?.showConfirm) {
      ok = await new Promise<boolean>((resolve) => tg.showConfirm(confirmText, (v: boolean) => resolve(!!v)));
    } else {
      ok = confirm(confirmText);
    }
    if (!ok) return;
    haptic("medium");
    dealPinging = true;
    try {
      const r = await api.pokeDeal(deal.amo_id);
      const parts: string[] = [];
      if (r.note_added) parts.push("заметка");
      if (r.urgent_task_amo_id) parts.push("срочная задача");
      if (r.sms && r.sms.ok) parts.push("SMS");
      const msg = `✓ Пнул ${r.manager_name}: ${parts.join(" + ") || "—"}`;
      if (tg?.showAlert) tg.showAlert(msg);
      else showToast(msg);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ошибка";
      if (tg?.showAlert) tg.showAlert(`Не удалось пнуть: ${msg}`);
      else showToast(msg);
    } finally {
      dealPinging = false;
    }
  }

  // Sort tasks: overdue first (biggest overdue at top), then by complete_till
  function sortTasks(tasks: OpenTask[]): OpenTask[] {
    const now = Date.now();
    return [...tasks].sort((a, b) => {
      const aOver = a.complete_till ? Math.max(0, (now - new Date(a.complete_till).getTime()) / 86400000) : 0;
      const bOver = b.complete_till ? Math.max(0, (now - new Date(b.complete_till).getTime()) / 86400000) : 0;
      if (aOver !== bOver) return bOver - aOver;  // bigger overdue first
      return (a.complete_till || "9999").localeCompare(b.complete_till || "9999");
    });
  }

  function priceFmt(rub: number | null): string {
    if (rub === null) return "—";
    if (rub >= 1_000_000) return `${(rub / 1_000_000).toFixed(2).replace(/\.?0+$/, "")} млн ₽`;
    if (rub >= 1000) return `${Math.floor(rub / 1000)}к ₽`;
    return `${rub} ₽`;
  }

  function dateFmt(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("ru", { day: "numeric", month: "short", year: "numeric" });
  }

  function shortDateTime(iso: string): string {
    const d = new Date(iso);
    const date = d.toLocaleDateString("ru", { day: "numeric", month: "short" });
    const time = d.toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" });
    return `${date}, ${time}`;
  }

  function dueFmt(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    const now = new Date();
    const deltaMs = d.getTime() - now.getTime();
    const dt = shortDateTime(iso);
    if (deltaMs < 0) {
      const overdueDays = Math.floor(-deltaMs / 86400000);
      if (overdueDays >= 1) return `просрочено ${overdueDays} дн · ${dt}`;
      const overdueHours = Math.floor(-deltaMs / 3600000);
      if (overdueHours >= 1) return `просрочено ${overdueHours}ч · ${dt}`;
      return `просрочено · ${dt}`;
    }
    if (deltaMs < 3600000) return `через ${Math.floor(deltaMs / 60000)} мин · ${dt}`;
    if (deltaMs < 86400000) return `через ${Math.floor(deltaMs / 3600000)}ч · ${dt}`;
    return dt;
  }

  // Friendly custom-field labels for the most common kvin fields.
  // Field IDs come from API audit (Kvin/docs/api-audit-2026-05-19/04_custom_fields.json).
  const CF_LABELS: Record<string, string> = {
    "462559": "Направление",
    "485043": "Форма оплаты",
    "485049": "Желаемая дата",
    "485053": "Комментарий",
    "557261": "Встреча офис: дата",
    "557273": "Встреча офис: время",
    "562043": "Выезд: дата",
    "562045": "Выезд: время",
    "562053": "Выезд: кто",
    "562119": "Доставка: дата",
    "608189": "Сумма (товары)",
    "608191": "Сумма (работы)",
    "608183": "Срок (работы)",
    "638829": "Где использовать",
    "638831": "Тип камина",
    "638835": "Стиль",
    "638837": "Расположение",
    "638839": "Когда нужен",
    "641069": "Контрагент",
  };

  // Date CFs are stored as unix ts (00:00 MSK) — show them as a calendar date.
  const DATE_CF = new Set(["485049", "557261", "562043", "562119"]);
  function cfValue(id: string, v: unknown): string {
    if (DATE_CF.has(id) && Number.isFinite(Number(v)) && Number(v) > 0) {
      return new Date((Number(v) + 3 * 3600) * 1000)
        .toLocaleDateString("ru", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
    }
    return String(v);
  }

  function cfEntries(cf: Record<string, string[]>) {
    const out: Array<{ id: string; label: string; values: string[] }> = [];
    for (const [id, values] of Object.entries(cf)) {
      if (!values || values.length === 0) continue;
      if (!CF_LABELS[id]) continue; // skip noisy tracking fields
      const cleaned = values.filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
      if (cleaned.length === 0) continue;
      out.push({ id, label: CF_LABELS[id], values: cleaned.map((v) => cfValue(id, v)) });
    }
    return out;
  }

  function mergePending(fresh: DealCard): DealCard {
    if (pendingTasks.length === 0) return fresh;
    const serverIds = new Set(fresh.open_tasks.map((t) => t.amo_id));
    // Drop pending that server now knows about
    pendingTasks = pendingTasks.filter((t) => !serverIds.has(t.amo_id));
    if (pendingTasks.length === 0) return fresh;
    return { ...fresh, open_tasks: [...fresh.open_tasks, ...pendingTasks] };
  }

  async function loadDeal() {
    try {
      const fresh = await api.dealCard(leadAmoId);
      deal = mergePending(fresh);
      if (fresh.can_edit && !meta) {
        api.dealsMeta().then((m) => (meta = m)).catch(() => {});
      }
      // Build team list from /api/team (full active team) — was previously
      // only responsibles-of-open-tasks + self, so picker showed 1 person
      // когда задач у сделки нет. Now все active manager + director.
      try {
        const t = await api.team();
        team = t.items.map((m) => ({
          amo_id: m.amo_id,
          name: m.is_me ? m.name + " (я)" : m.name,
        }));
      } catch {
        // Fallback to old logic if /api/team fails
        const ids = new Map<number, string>();
        for (const t of deal?.open_tasks ?? []) {
          if (t.responsible_user_amo_id && t.responsible_name) {
            ids.set(t.responsible_user_amo_id, t.responsible_name);
          }
        }
        try {
          const me = await api.me();
          if (me.amo_user_amo_id && me.amo_user_name) {
            ids.set(me.amo_user_amo_id, me.amo_user_name + " (я)");
          }
        } catch {}
        team = Array.from(ids.entries()).map(([amo_id, name]) => ({ amo_id, name }));
      }
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка загрузки";
    } finally {
      loading = false;
    }
  }

  function pollUntilSettled() {
    // Poll every 4s up to 8 times — covers typical amo → webhook → DB lag (~10–30s)
    let tries = 0;
    const tick = async () => {
      if (pendingTasks.length === 0 || tries >= 8) return;
      tries++;
      await loadDeal();
      if (pendingTasks.length > 0) setTimeout(tick, 4000);
    };
    setTimeout(tick, 4000);
  }

  onMount(() => {
    loadDeal();
    // Listen for voice/text-created tasks on this deal — refresh + poll
    const onDealChanged = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      if (detail.leadAmoId === leadAmoId) {
        loadDeal();
        // Voice path doesn't have a known task_amo_id for optimistic merge,
        // so just poll a few times to catch the new task once webhook syncs.
        let tries = 0;
        const tick = async () => {
          tries++;
          await loadDeal();
          if (tries < 6) setTimeout(tick, 4000);
        };
        setTimeout(tick, 3000);
      }
    };
    window.addEventListener("kvin:deal-changed", onDealChanged);
    return () => window.removeEventListener("kvin:deal-changed", onDealChanged);
  });

  let removingTask = $state<number | null>(null);
  let taskMenuFor = $state<number | null>(null);

  async function completeTaskInDeal(taskAmoId: number) {
    haptic("medium");
    removingTask = taskAmoId;
    taskMenuFor = null;
    try {
      await api.completeTask(taskAmoId);
      if (deal) {
        deal = { ...deal, open_tasks: deal.open_tasks.filter((t) => t.amo_id !== taskAmoId) };
      }
      pendingTasks = pendingTasks.filter((t) => t.amo_id !== taskAmoId);
    } catch (e) {
      showToast(`${e instanceof Error ? e.message : "ошибка"}`);
    } finally {
      removingTask = null;
    }
  }
  async function deleteTaskInDeal(taskAmoId: number, text: string) {
    if (!confirm(`Удалить задачу?\n\n«${text.slice(0, 80)}»\n\nЗадача будет закрыта в amoCRM с пометкой «Удалено через Квин» и исчезнет из всех списков.`)) return;
    haptic("medium");
    removingTask = taskAmoId;
    taskMenuFor = null;
    try {
      await api.deleteTask(taskAmoId);
      if (deal) {
        deal = { ...deal, open_tasks: deal.open_tasks.filter((t) => t.amo_id !== taskAmoId) };
      }
      pendingTasks = pendingTasks.filter((t) => t.amo_id !== taskAmoId);
    } catch (e) {
      alert(`Не удалось удалить: ${e instanceof Error ? e.message : "ошибка"}`);
    } finally {
      removingTask = null;
    }
  }

  let togglingInstaller = $state(false);
  async function toggleInstaller() {
    if (!deal) return;
    haptic("medium");
    togglingInstaller = true;
    const wasOn = !!deal.installer_manual;
    try {
      if (wasOn) await api.unassignInstaller(leadAmoId);
      else await api.assignInstaller(leadAmoId);
      deal = { ...deal, installer_manual: !wasOn };
      showToast(wasOn ? "Снято с монтажа" : "🔧 Отправлено на монтаж");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "ошибка");
    } finally {
      togglingInstaller = false;
    }
  }

  async function archiveDeal() {
    if (!deal) return;
    const tg = (window as any).Telegram?.WebApp;
    const confirmText = `Скрыть «${deal.name.slice(0, 60)}» из списков?\n\nВ amoCRM сделка останется. Вернуть можно через Мои сделки → Архив.`;
    let ok = false;
    if (tg?.showConfirm) {
      ok = await new Promise<boolean>((resolve) => tg.showConfirm(confirmText, (v: boolean) => resolve(!!v)));
    } else {
      ok = confirm(confirmText);
    }
    if (!ok) return;
    haptic("medium");
    try {
      await api.archiveDeal(leadAmoId);
      // Hard reload — гарантирует, что список «Мои сделки» перетянется без archived.
      // Раньше делали setTimeout(onhome, 600) → onhome переключал view без refetch,
      // юзер видел ту же сделку в списке и думал, что архив не сработал.
      const dest = "/?view=my-deals&sort=new&t=" + Date.now();
      if (tg?.showAlert) {
        tg.showAlert(`✓ «${deal.name.slice(0, 50)}» в архиве`, () => { window.location.href = dest; });
      } else {
        showToast("Сделка убрана в архив");
        setTimeout(() => { window.location.href = dest; }, 800);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ошибка";
      if (tg?.showAlert) tg.showAlert(`Не удалось убрать в архив: ${msg}`);
      else showToast(msg);
    }
  }

  async function deleteDeal() {
    if (!deal) return;
    const msg = `Удалить сделку «${deal.name.slice(0, 60)}» из amoCRM?\n\nСделка и её задачи удалятся насовсем. Для спама и рекламы.`;
    if (!(await tgConfirm(msg))) return;
    haptic("heavy");
    try {
      await api.deleteDeal(leadAmoId);
      const dest = "/?view=my-deals&sort=new&t=" + Date.now();
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.showAlert) {
        tg.showAlert(`Сделка «${deal.name.slice(0, 50)}» удалена`, () => { window.location.href = dest; });
      } else {
        showToast("Сделка удалена");
        setTimeout(() => { window.location.href = dest; }, 800);
      }
    } catch (e) {
      tgAlert(`Не удалось удалить: ${errText(e)}`);
    }
  }

  async function createTask(input: { text: string; responsibleAmoId: number; completeTillIso: string }) {
    haptic("medium");
    try {
      const result = await api.createTaskOnDeal(leadAmoId, input.text, input.responsibleAmoId, input.completeTillIso);
      const name = team.find((t) => t.amo_id === input.responsibleAmoId)?.name ?? "менеджеру";
      showToast(`Поставил ${name}: ${input.text.slice(0, 40)}`);

      if (deal && result.task_amo_id) {
        const optimistic: OpenTask = {
          amo_id: result.task_amo_id,
          text: input.text,
          complete_till: input.completeTillIso,
          responsible_user_amo_id: input.responsibleAmoId,
          responsible_name: name.replace(" (я)", ""),
          tg_pingable: false,
        };
        pendingTasks = [...pendingTasks, optimistic];
        deal = { ...deal, open_tasks: [...deal.open_tasks, optimistic] };
        // Poll until webhook syncs the real task into our DB
        pollUntilSettled();
      }
    } catch (e) {
      showToast(`${e instanceof Error ? e.message : "ошибка"}`);
    }
  }

  function tgAlert(msg: string) {
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.showAlert) tg.showAlert(msg);
    else showToast(msg, 4000);
  }

  async function tgConfirm(msg: string): Promise<boolean> {
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.showConfirm) return new Promise((resolve) => tg.showConfirm(msg, (v: boolean) => resolve(!!v)));
    return confirm(msg);
  }

  function errText(e: unknown) {
    return e instanceof Error ? e.message : "ошибка";
  }

  async function moveStage(pipelineId: number, statusId: number, stageName: string) {
    if (!deal) return;
    if (statusId === 142 || statusId === 143) {
      const verb = statusId === 142 ? "Закрыть как успешную" : "Закрыть как нереализованную";
      const tasksNote = deal.open_tasks.length ? `\n\nОткрытые задачи (${deal.open_tasks.length}) будут сняты.` : "";
      if (!(await tgConfirm(`${verb}?\n\n«${deal.name.slice(0, 60)}»${tasksNote}`))) return;
    }
    haptic("medium");
    const prev = { stage_name: deal.stage_name, status_amo_id: deal.status_amo_id, pipeline_amo_id: deal.pipeline_amo_id };
    deal = { ...deal, stage_name: stageName, status_amo_id: statusId, pipeline_amo_id: pipelineId };
    moving = true;
    try {
      const res = await api.updateDeal(leadAmoId, { pipeline_amo_id: pipelineId, status_amo_id: statusId });
      showToast(res.closed_tasks ? `→ ${stageName} · снято задач: ${res.closed_tasks}` : `→ ${stageName}`);
      await loadDeal();
    } catch (e) {
      deal = { ...deal, ...prev };
      tgAlert(`Не удалось перенести: ${errText(e)}`);
    } finally {
      moving = false;
    }
  }

  async function saveEdit(patch: DealPatch): Promise<boolean> {
    try {
      await api.updateDeal(leadAmoId, patch);
      showToast("Сохранено в amoCRM");
      await loadDeal();
      return true;
    } catch (e) {
      tgAlert(`Не удалось сохранить: ${errText(e)}`);
      return false;
    }
  }

  function openContact(c: { amo_id: number; name: string; phone: string | null } | null) {
    editingContact = c;
    contactSheetOpen = true;
  }

  async function saveContact(v: { name: string; phone: string }): Promise<boolean> {
    try {
      if (editingContact) {
        await api.updateDealContact(leadAmoId, editingContact.amo_id, v);
        showToast("Контакт сохранён в amoCRM");
      } else {
        const r = await api.addDealContact(leadAmoId, v);
        showToast(r.reused ? "Нашёл клиента с этим номером и привязал" : "Контакт добавлен");
      }
      await loadDeal();
      return true;
    } catch (e) {
      tgAlert(`Не удалось сохранить контакт: ${errText(e)}`);
      return false;
    }
  }

  async function addNote(text: string): Promise<boolean> {
    try {
      await api.addDealNote(leadAmoId, text);
      showToast("Заметка добавлена");
      return true;
    } catch (e) {
      tgAlert(`Не удалось добавить заметку: ${errText(e)}`);
      return false;
    }
  }

  // Stage strip like amoCRM's pipeline bar: open stages of the deal's pipeline.
  const strip = $derived.by(() => {
    if (!deal || !meta) return null;
    const d = deal;
    const p = meta.pipelines.find((x) => x.amo_id === d.pipeline_amo_id);
    if (!p) return null;
    const openStages = p.statuses.filter((s) => s.amo_id !== 142 && s.amo_id !== 143);
    const idx = openStages.findIndex((s) => s.amo_id === d.status_amo_id);
    const closed = d.status_amo_id === 142 ? "won" : d.status_amo_id === 143 ? "lost" : null;
    return { total: openStages.length, idx, closed };
  });

  const fields = $derived(deal ? cfEntries(deal.custom_fields) : []);
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">Карточка сделки</div>
    {#if onhome}
      <button class="home" onclick={() => onhome?.()} aria-label="Главная">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        </svg>
      </button>
    {:else}
      <div class="spacer"></div>
    {/if}
  </header>

  {#if loading}
    <div class="loading">Загружаю…</div>
  {:else if error}
    <div class="error-box">{error}</div>
  {:else if deal}
    <section class="hero">
      <div class="name">{deal.name}</div>
      <div class="meta">
        {#if deal.price !== null}
          <span class="price">{priceFmt(deal.price)}</span>
        {/if}
        {#if deal.stage_name && !(deal.can_edit && meta)}
          <span class="sep"></span>
          <span class="stage">{deal.stage_name}</span>
        {/if}
      </div>
      {#if deal.can_edit && meta}
        <button class="stage-btn" class:won={strip?.closed === "won"} class:lost={strip?.closed === "lost"}
                onclick={() => (stageSheetOpen = true)} disabled={moving} type="button"
                aria-label="Сменить этап">
          <span class="stage-top">
            <span class="stage-name">{deal.stage_name ?? "Этап"}</span>
            <span class="stage-change">{moving ? "…" : "Сменить"}</span>
          </span>
          {#if strip && strip.total > 0}
            <span class="strip" aria-hidden="true">
              {#each Array(strip.total) as _, i}
                <span class="seg" class:on={strip.closed === "won" || i <= strip.idx}></span>
              {/each}
            </span>
          {/if}
        </button>
      {/if}
      {#if deal.responsible_name}
        <div class="resp-row">
          <div class="resp">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            {deal.responsible_name}
          </div>
          {#if role === "director"}
            <button class="deal-ping-btn"
                    class:loading={dealPinging}
                    disabled={dealPinging}
                    onclick={pingDeal}>
              {dealPinging ? "…" : "Пнуть"}
            </button>
          {/if}
        </div>
      {/if}
      <div class="dates">
        Создана {dateFmt(deal.created_at)}{#if deal.updated_at} · обновлена {dateFmt(deal.updated_at)}{/if}
      </div>
    </section>

    {#if deal.contacts.length > 0 || deal.can_edit}
      <div class="section-title">Контакт</div>
      <section class="contacts">
        {#each deal.contacts as c}
          <div class="contact">
            <div class="contact-head">
              <div class="cname">{c.name}</div>
              {#if deal.can_edit}
                <button class="contact-edit" type="button" onclick={() => openContact(c)}>Изменить</button>
              {/if}
            </div>
            <div class="ctel">
              {#if c.phone}<a href={`tel:${c.phone}`}>{c.phone}</a>{/if}
              {#if c.phone && c.email} · {/if}
              {#if c.email}<a href={`mailto:${c.email}`}>{c.email}</a>{/if}
            </div>
          </div>
        {/each}
        {#if deal.can_edit}
          <button class="contact-add" type="button" onclick={() => openContact(null)}>+ Добавить контакт</button>
        {/if}
      </section>
    {/if}

    <div class="cta-bar" class:with-delete={role === "director"}>
      <button class="cta-add-task" onclick={() => (taskSheetOpen = true)}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
             stroke-width="2.6" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
        Поставить задачу
      </button>
      <button class="cta-secondary" onclick={archiveDeal}>В архив</button>
      {#if role === "director"}
        <button class="cta-secondary cta-danger" onclick={deleteDeal} aria-label="Удалить сделку">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/></svg>
          Удалить
        </button>
      {/if}
    </div>

    {#if deal.can_edit}
      <div class="edit-bar">
        <button class="cta-tool" onclick={() => (editSheetOpen = true)} disabled={!meta} type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z"/></svg>
          Изменить
        </button>
        <button class="cta-tool" onclick={() => (noteSheetOpen = true)} type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
          Заметка
        </button>
      </div>
    {/if}

    {#if role === "director"}
      <div class="installer-bar">
        <button class="cta-installer" class:on={deal.installer_manual}
                disabled={togglingInstaller} onclick={toggleInstaller}>
          {#if togglingInstaller}…{:else if deal.installer_manual}🔧 Снять с монтажа{:else}🔧 На монтаж{/if}
        </button>
        {#if deal.installer_stage && !deal.installer_manual}
          <span class="installer-hint">монтажник уже видит её — стадия Доставка/Реализация</span>
        {/if}
      </div>
    {/if}

    <CallsBlock calls={deal.calls ?? []} />

    <div class="section-title">Открытые задачи · {deal.open_tasks.length}</div>

    {#if deal.open_tasks.length > 0}
      <section class="tasks">
        {#each sortTasks(deal.open_tasks) as t}
          {@const overdueDays = t.complete_till ? Math.max(0, Math.floor((Date.now() - new Date(t.complete_till).getTime()) / 86400000)) : 0}
          {@const isOverdue = !!(t.complete_till && new Date(t.complete_till).getTime() < Date.now())}
          <div class="task" class:overdue={isOverdue}>
            {#if isOverdue && overdueDays > 0}
              <div class="ovd-badge">{overdueDays}<span class="ovd-unit">дн</span></div>
            {:else if isOverdue}
              <div class="ovd-badge small">⚠</div>
            {/if}
            <div class="task-main">
              <div class="ttext">{t.text}</div>
              <div class="tmeta">
                {#if t.responsible_name}<span class="resp">{t.responsible_name}</span>{/if}
                {#if t.responsible_name && t.complete_till}<span class="sep"></span>{/if}
                {#if t.complete_till}
                  <span class="tdue" class:overdue={isOverdue}>{dueFmt(t.complete_till)}</span>
                {/if}
              </div>
            </div>
            {#if isOverdue && role === "director"}
              <button class="ping-btn"
                      class:loading={pinging === t.amo_id}
                      disabled={pinging === t.amo_id}
                      onclick={() => pingTask(t)}>
                {pinging === t.amo_id ? "…" : "Пнуть"}
              </button>
            {/if}
            {#if role === "director"}
              <button class="task-kebab" type="button" aria-label="Действия"
                      disabled={removingTask === t.amo_id}
                      onclick={(e) => { e.stopPropagation(); taskMenuFor = taskMenuFor === t.amo_id ? null : t.amo_id; }}>
                {#if removingTask === t.amo_id}⋯{:else}⋮{/if}
              </button>
              {#if taskMenuFor === t.amo_id}
                <div class="task-menu" onclick={(e) => e.stopPropagation()} role="menu">
                  {#if isOverdue}
                    <button onclick={() => { taskMenuFor = null; pingTask(t); }} type="button" class="poke">🔔 Пнуть {t.responsible_name ?? "менеджера"}</button>
                  {/if}
                  <button onclick={() => completeTaskInDeal(t.amo_id)} type="button">Снять (выполнить)</button>
                  <button onclick={() => deleteTaskInDeal(t.amo_id, t.text)} type="button" class="danger">Удалить</button>
                  <button onclick={() => taskMenuFor = null} type="button" class="ghost">Отмена</button>
                </div>
              {/if}
            {/if}
          </div>
        {/each}
      </section>
    {/if}

    {#if meta}
      <StageSheet bind:open={stageSheetOpen} pipelines={meta.pipelines}
                  currentPipelineId={deal.pipeline_amo_id} currentStatusId={deal.status_amo_id}
                  onpick={moveStage} />
      <DealEditSheet bind:open={editSheetOpen} {deal} fields={meta.fields} {team}
                     canReassign={!!deal.can_reassign} onsave={saveEdit} />
    {/if}
    <NoteSheet bind:open={noteSheetOpen} onsubmit={addNote} />
    <ContactSheet
      bind:open={contactSheetOpen}
      title={editingContact ? "Изменить контакт" : "Новый контакт"}
      name={editingContact?.name ?? ""}
      phone={editingContact?.phone ?? ""}
      onsave={saveContact}
    />

    <TaskCreateSheet bind:open={taskSheetOpen} {team} defaultResponsibleAmoId={deal?.responsible_user_amo_id ?? undefined} onsubmit={createTask} />

    {#if fields.length > 0 || (deal.can_edit && meta)}
      <div class="section-head">
        <div class="section-title">Поля сделки</div>
        {#if deal.can_edit && meta}
          <button class="section-link" onclick={() => (editSheetOpen = true)} type="button">Изменить</button>
        {/if}
      </div>
      <section class="fields">
        {#each fields as f}
          <div class="field">
            <div class="flab">{f.label}</div>
            <div class="fval">{f.values.join(", ")}</div>
          </div>
        {/each}
      </section>
    {/if}

    {#if deal.amo_url}
      <div class="actions">
        <a class="amo-link" href={deal.amo_url} target="_blank" rel="noopener noreferrer">
          Открыть в amoCRM →
        </a>
      </div>
    {/if}
  {/if}

  {#if toast}
    <div class="dc-toast">{toast}</div>
  {/if}
</div>

<style>
  .screen { padding-bottom: 100px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute);
  }
  .back:active, .home:active { background: var(--bg-elev); }
  .back svg, .home svg { width: 20px; height: 20px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-size: 16px; font-weight: 700;
    flex: 1; text-align: center;
  }
  .spacer { width: 36px; }

  .loading, .error-box {
    padding: 40px 16px; text-align: center;
    color: var(--text-mute); font-size: 13px;
  }
  .error-box { color: var(--danger); }

  .hero { padding: 16px; }
  .name {
    font-family: 'Manrope', sans-serif;
    font-size: 18px; font-weight: 700; line-height: 1.3;
  }
  .meta {
    display: flex; align-items: center; gap: 8px;
    margin-top: 8px;
    font-size: 13px;
  }
  .price {
    font-family: 'JetBrains Mono', monospace;
    color: var(--brass); font-weight: 700;
  }
  .stage { color: var(--text-mute); }
  .sep {
    width: 3px; height: 3px;
    background: var(--text-dim); border-radius: 50%;
  }
  .resp-row {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; margin-top: 8px;
  }
  .resp {
    display: flex; align-items: center; gap: 6px;
    font-size: 13px; color: var(--text);
    font-weight: 500;
  }
  .resp svg { width: 14px; height: 14px; color: var(--brass); }
  .deal-ping-btn {
    padding: 6px 12px;
    background: var(--bg-elev-2);
    border: 1px solid var(--ember);
    border-radius: 8px;
    color: var(--ember);
    font-family: 'Manrope', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
  }
  .deal-ping-btn:active { background: var(--ember); color: #fff; }
  .deal-ping-btn.loading { opacity: 0.6; cursor: wait; }
  .dates {
    margin-top: 6px;
    font-size: 11px; color: var(--text-dim);
  }

  .section-title {
    padding: 18px 16px 8px;
    font-family: 'Manrope', sans-serif;
    font-size: 11px; font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--text-dim);
  }
  .cta-bar {
    padding: 14px 16px 0;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
  }
  .cta-add-task {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    background: var(--ember);
    color: #fff;
    border: none;
    border-radius: 14px;
    padding: 14px 18px;
    font-family: 'Manrope', sans-serif;
    font-size: 15px; font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(232, 124, 47, 0.32);
    transition: filter 120ms, transform 100ms;
  }
  .cta-add-task:active { transform: scale(0.985); filter: brightness(0.92); }
  .cta-add-task svg { flex-shrink: 0; }
  .cta-secondary {
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 14px;
    padding: 0 16px;
    color: var(--text-mute);
    font-family: 'Manrope', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
  }
  .cta-secondary:active { transform: scale(0.97); }
  .cta-secondary:hover { color: var(--text); border-color: var(--ember); }
  .cta-bar.with-delete { grid-template-columns: 1fr auto auto; }
  .cta-danger { display: flex; align-items: center; gap: 6px; color: var(--danger); }
  .cta-danger:hover { color: var(--danger); border-color: var(--danger); }

  .stage-btn {
    width: 100%; margin-top: 12px;
    display: flex; flex-direction: column; gap: 8px;
    padding: 10px 12px; border-radius: 12px;
    background: var(--bg-elev); border: 1px solid var(--border);
    color: var(--text); text-align: left; cursor: pointer;
    transition: border-color 120ms, transform 100ms;
  }
  .stage-btn:active { transform: scale(0.985); }
  .stage-btn:disabled { opacity: 0.6; cursor: wait; }
  .stage-top { display: flex; align-items: center; gap: 10px; }
  .stage-name { flex: 1; font-size: 14px; font-weight: 600; min-width: 0; }
  .stage-change { font-size: 12px; font-weight: 600; color: var(--ember); flex-shrink: 0; }
  .strip { display: flex; gap: 3px; }
  .seg { flex: 1; height: 4px; border-radius: 2px; background: var(--border-strong); }
  .seg.on { background: var(--ember); }
  .stage-btn.won .seg.on { background: var(--success); }
  .stage-btn.won .stage-name { color: var(--success); }
  .stage-btn.lost .stage-name { color: var(--danger); }

  .edit-bar { padding: 8px 16px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .cta-tool {
    height: 44px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    background: var(--bg); border: 1.5px solid var(--border-strong, var(--border));
    color: var(--text); font-family: 'Manrope', sans-serif; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: transform 100ms, border-color 120ms;
  }
  .cta-tool svg { width: 16px; height: 16px; color: var(--brass); }
  .cta-tool:active { transform: scale(0.97); }
  .cta-tool:disabled { opacity: 0.5; cursor: wait; }

  .section-head { display: flex; align-items: baseline; justify-content: space-between; padding-right: 16px; }
  .section-link {
    background: none; border: none; padding: 4px 0;
    color: var(--ember); font-size: 13px; font-weight: 600; cursor: pointer;
  }

  .installer-bar { padding: 8px 16px 0; display: flex; flex-direction: column; gap: 4px; }
  .cta-installer {
    height: 44px; border-radius: 12px;
    background: var(--bg); border: 1.5px solid var(--border-strong, var(--border));
    color: var(--text); font-family: 'Manrope', sans-serif;
    font-size: 14px; font-weight: 600; cursor: pointer; transition: all 120ms;
  }
  .cta-installer:active { transform: scale(0.98); }
  .cta-installer.on { background: var(--ember); color: #fff; border-color: var(--ember); }
  .cta-installer:disabled { opacity: 0.5; cursor: wait; }
  .installer-hint { font-size: 11px; color: var(--text-dim); text-align: center; }

  .contacts, .tasks, .fields {
    padding: 0 16px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .contact, .task, .field {
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: var(--shadow-sm);
  }
  .cname, .ttext, .flab {
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 14px;
  }
  .flab {
    font-size: 11px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .ctel {
    margin-top: 4px;
    font-size: 13px;
    color: var(--text-mute);
  }
  .ctel a { color: var(--ember); }
  .contact-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .contact-edit, .contact-add {
    background: none; border: none; padding: 6px 0;
    color: var(--ember); font-size: 13px; font-weight: 600; cursor: pointer;
  }
  .contact-add { display: block; padding: 10px 0 2px; }
  .contact-edit:active, .contact-add:active { opacity: 0.6; }
  .task { display: flex; gap: 10px; align-items: center; position: relative; }
  .task.overdue { border-left: 3px solid var(--danger); }
  .task-kebab {
    flex-shrink: 0;
    width: 32px; height: 36px;
    background: transparent;
    border: 1px solid var(--border);
    border-radius: 10px;
    color: var(--text-mute);
    font-size: 20px; font-weight: 700; line-height: 1;
    cursor: pointer;
    transition: all 120ms;
  }
  .task-kebab:active { transform: scale(0.94); }
  .task-kebab:hover { color: var(--text); border-color: var(--ember); }
  .task-kebab:disabled { opacity: 0.5; cursor: wait; }
  .task-menu {
    position: absolute;
    right: 8px; top: calc(100% - 4px);
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: var(--shadow-lg);
    z-index: 30; min-width: 200px; overflow: hidden;
  }
  .task-menu button {
    display: block; width: 100%;
    background: transparent; border: none;
    text-align: left;
    padding: 12px 14px;
    color: var(--text);
    font-family: 'Manrope', sans-serif;
    font-size: 14px; cursor: pointer;
  }
  .task-menu button:not(:last-child) { border-bottom: 1px solid var(--border); }
  .task-menu button:active { background: var(--bg-elev-2); }
  .task-menu button.danger { color: var(--danger); }
  .task-menu button.ghost { color: var(--text-mute); }
  .task-menu button.poke { color: var(--ember); font-weight: 600; }
  .task-main { flex: 1; min-width: 0; }
  .ovd-badge {
    flex-shrink: 0;
    min-width: 48px;
    height: 48px;
    border-radius: 10px;
    background: var(--danger);
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 17px;
    line-height: 1;
    padding: 0 6px;
  }
  .ovd-unit { font-size: 9px; margin-top: 2px; opacity: 0.85; font-weight: 500; }
  .tmeta {
    margin-top: 4px;
    display: flex; align-items: center; gap: 8px;
    flex-wrap: wrap;
    font-size: 12px; color: var(--text-mute);
  }
  .resp { color: var(--brass); font-weight: 600; }
  .tdue { color: var(--text-mute); }
  .tdue.overdue { color: var(--danger); font-weight: 600; }
  .sep {
    width: 3px; height: 3px;
    background: var(--text-dim);
    border-radius: 50%;
  }

  .ping-btn {
    flex-shrink: 0;
    padding: 8px 12px;
    background: var(--bg-elev-2);
    border: 1px solid var(--ember);
    border-radius: 10px;
    color: var(--ember);
    font-family: 'Manrope', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 120ms;
  }
  .ping-btn:active { background: var(--ember); color: #fff; }
  .ping-btn.loading { opacity: 0.6; cursor: wait; }
  .ping-btn:disabled { cursor: not-allowed; }
  .fval {
    margin-top: 4px;
    font-size: 13px;
    color: var(--text);
    line-height: 1.4;
  }

  .actions {
    padding: 20px 16px 8px;
  }
  .amo-link {
    display: block;
    text-align: center;
    padding: 12px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    color: var(--ember);
    font-weight: 600;
    font-size: 14px;
  }

  .dc-toast {
    position: fixed;
    left: 50%;
    top: calc(60px + var(--safe-top, 0px));
    transform: translateX(-50%);
    background: var(--bg-elev);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: var(--shadow-lg);
    z-index: 100;
    max-width: 88%;
    text-align: center;
  }
</style>
